import { ChangeDetectorRef, Component, OnDestroy, ViewChild } from '@angular/core';
import { Observable, Subject, Subscription } from 'rxjs';
import { Store } from '@ngrx/store';
import { ActivatedRoute } from '@angular/router';
import { Shippings } from '../../../types';
import { FormService } from '../../../services';
import { CheckoutActions } from '../../../checkout-store/actions';
import * as fromCheckoutReducer from '../../../checkout-store/reducers/checkout.reducer';
import { finalize, takeUntil } from 'rxjs/operators';
import { Actions, ofType } from '@ngrx/effects';
import { PdqToastComponent } from '@app/storybook/components/toast/toast.component';
import { ToastService } from '@app/storybook/services/toast/toast.service';
import { CheckoutAppComponent } from '@pages/checkout-app/checkout-layout/main/components/delivery-rates/checkout-form/checkout.app.component';
import { UnsavedChangesAware } from '@pages/checkout-app/shared/deactivate-guard/deactivate-gurad';
import { ItemFeeSetupStateService } from '@pages/checkout-app/services/item-fee-setup-state.service';

@Component({
  selector: 'app-edit-checkout',
  templateUrl: './edit-checkout.html',
  styleUrls: ['./edit-checkout.scss'],
  standalone: false,
})
export class EditCheckout implements OnDestroy, UnsavedChangesAware {
  @ViewChild(CheckoutAppComponent) checkoutForm?: CheckoutAppComponent;

  editableCheckout$: Observable<Shippings>;
  editSubscription: Subscription;
  editCheckoutData: Shippings;
  editLoading = false;
  isLoadingCheckout: boolean = false;
  private checkoutId: string;
  private queryParamsSubscription$: Subscription;
  private destroyed$ = new Subject<boolean>();

  constructor(
    private store: Store<fromCheckoutReducer.State>,
    private route: ActivatedRoute,
    private changeDetectorRef: ChangeDetectorRef,
    private formService: FormService,
    private detectSuccessOrFailure$: Actions,
    private toastService: ToastService,
    private itemFeeSetupState: ItemFeeSetupStateService,
  ) {
    this.detectStoreActions();
    this.queryParamsSubscription$ = route.queryParams.subscribe((queryParam: any) => {
      this.loadCheckout(queryParam.checkoutId);
    });

    this.editSubscription = this.editableCheckout$.subscribe((shippings: Shippings) => {
      this.editCheckoutData = shippings;
    });
  }

  detectStoreActions() {
    this.detectSuccessOrFailure$
      .pipe(takeUntil(this.destroyed$), ofType(CheckoutActions.editCheckoutSuccess))
      .subscribe(() => {
        this.store.dispatch(CheckoutActions.loadCheckouts());
        this.itemFeeSetupState.notifyMethodSaved();
        this.editLoading = false;
        this.toastService.show(PdqToastComponent, {
          message: `Delivery method has been edited Successfully`,
          mode: 'info',
          duration: 5000,
        });
      });

    this.detectSuccessOrFailure$
      .pipe(takeUntil(this.destroyed$), ofType(CheckoutActions.editCheckoutFailure))
      .subscribe((res) => {
        this.editLoading = false;
        const stateMsg = res?.type?.split(']')[1];
        // api error body is { message }, not a string
        const error = res?.errorMessage as unknown as string | { message?: string | string[] } | undefined;
        const apiMessage = typeof error === 'string' ? error : error?.message;
        this.toastService.show(PdqToastComponent, {
          message: (Array.isArray(apiMessage) ? apiMessage.join(', ') : apiMessage) || `${stateMsg}`,
          mode: 'danger',
          duration: 5000,
        });
      });
  }

  private loadCheckout(checkoutId: string) {
    this.editLoading = true;
    this.editableCheckout$ = this.formService
      .loadCheckoutById(checkoutId)
      .pipe(finalize(() => (this.editLoading = false)));
  }

  public editCheckout(shippingsId: string, shippings: Shippings): void {
    this.store.dispatch(CheckoutActions.editCheckout({ shippings, shippingsId }));
  }

  hasUnsavedChanges(): boolean {
    return this.checkoutForm?.changeTracker.refresh() ?? true;
  }

  // no page loader: it rebuilds the open form and re-adds loaded list items
  onSubmit(values: Shippings): void {
    this.editCheckout(this.editCheckoutData.id, { ...values });
  }

  ngOnDestroy(): void {
    this.queryParamsSubscription$.unsubscribe();
    this.editSubscription.unsubscribe();
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }
}
